/**
 * @typedef {Object} HTMLElementZoomExtensions
 * @property {string} [_zoomKey]
 * @property {boolean} [_zoomInitialized]
 * @property {boolean} [_zoomResetBound]
 * @property {number} [_scale]
 * @property {Function} [_resetTransform]
 * @property {{ left: number, top: number }} [_originalPosition]
 * @property {number} [_touchStartTime]
 * @property {number} [_touchStartX]
 * @property {number} [_touchStartY]
 */

/**
 * @typedef {HTMLElement & HTMLElementZoomExtensions} ZoomableHTMLElement
 */

/**
 * @typedef {Object} ContainerOriginalStyles
 * @property {string} left
 * @property {string} right
 * @property {string} transform
 * @property {string} willChange
 */

/**
 * @typedef {Object} ContainerZoomExtensions
 * @property {ContainerOriginalStyles} [_originalStyles]
 * @property {boolean} [_hadComputedTransform]
 */

/**
 * @typedef {HTMLElement & ContainerZoomExtensions} ZoomableContainerElement
 */

import { getAnalyticsTracker } from '../../analytics/hangout-obs-tracker.js';

const zoomStateMap = new Map();
const CHARACTER_CONTAINER_SELECTOR = '[class*="character-image-"], .character-image';
const CHARACTER_MEDIA_SELECTOR = '[class*="character-image-"] > img, [class*="character-image-"] > video, .character-image > img, .character-image > video';
const PAN_ACTIVATION_THRESHOLD_PX = 2;
const MIN_SCALE = 0.7;
const MAX_SCALE = 3;
const ZOOM_TRACK_IDLE_MS = 400;
const RESET_MIN_VISIBLE_FRACTION = 0.25;

/** @type {Promise<import('./character-manager.js').characterManager> | null} */
let characterManagerPromise = null;

const getCharacterManager = () => {
    if (!characterManagerPromise) {
        characterManagerPromise = import('./character-manager.js').then((mod) => mod.characterManager);
    }
    return characterManagerPromise;
};

/** @param {Element} el */
const getCharacterContainer = (el) =>
    /** @type {ZoomableContainerElement | null} */ (
        el.closest('[class*="character-image-"]') || el.closest('.character-image')
    );

const queryCharacterMedia = () =>
    /** @type {NodeListOf<ZoomableHTMLElement>} */ (document.querySelectorAll(CHARACTER_MEDIA_SELECTOR));

const countCharacterContainers = () => {
    const multiCount = document.querySelectorAll('[class*="character-image-"]').length;
    if (multiCount > 0) return multiCount;
    return document.querySelector('.character-image:not([class*="character-image-"])') ? 1 : 0;
};

/** @param {ZoomableHTMLElement} img */
const getContainerIndexForImage = (img) => {
    const container = img.closest('[class*="character-image-"]');
    if (container) {
        const match = container.className.match(/character-image-(\d+)/);
        if (match) return parseInt(match[1], 10);
    }
    return 0;
};

/** @param {ZoomableHTMLElement} img */
const getCurrentScale = (img) => {
    const scaleMatch = (img.style.transform || '').match(/scale\(([^)]+)\)/);
    return scaleMatch ? parseFloat(scaleMatch[1]) : 1;
};

/**
 * @param {'zoom' | 'drag' | 'click'} kind
 * @param {ZoomableHTMLElement} img
 */
const trackSprite = (kind, img) => {
    const container = getCharacterContainer(img);
    const characterId = container ? parseInt(container.getAttribute('data-character-id'), 10) : NaN;
    if (!characterId) return;

    const tracker = getAnalyticsTracker();
    switch (kind) {
        case 'zoom':
            tracker.trackFeature('sprite_zoomed');
            return;
        case 'drag':
            tracker.trackFeature('sprite_dragged');
            return;
        case 'click':
            return;
        default: {
            const _exhaustive = /** @type {never} */ (kind);
            void _exhaustive;
        }
    }
};

/**
 * @param {ZoomableHTMLElement} img
 * @param {Event | { preventDefault: Function, stopPropagation: Function, target: Element }} event
 * @param {{ trackClick?: boolean }} [options]
 */
const triggerCharacterInteraction = async (img, event, options = {}) => {
    const container = getCharacterContainer(img);
    if (!container?.getAttribute('data-character-id')) return;

    if (options.trackClick) trackSprite('click', img);

    try {
        const manager = await getCharacterManager();
        await manager.handleCharacterClick(event, { playSound: false });
    } catch (error) {
        console.warn(`[Sprite Click] Key: ${img._zoomKey}, Error triggering character interaction:`, error);
    }
};

/** @param {ZoomableHTMLElement} img */
const constrainBottomEdge = (img) => {
    if (img.style.position !== 'fixed') return;

    const rect = img.getBoundingClientRect();
    const viewportBottom = window.innerHeight;
    if (rect.bottom >= viewportBottom) return;

    const currentTop = parseFloat(img.style.top) || 0;
    img.style.top = `${currentTop + (viewportBottom - rect.bottom)}px`;
};

/** @param {ZoomableContainerElement} container */
const restoreContainerOriginalStyles = (container) => {
    if (!container._originalStyles) return;
    container.style.left = container._originalStyles.left;
    container.style.right = container._originalStyles.right;
    if (container._hadComputedTransform) {
        container.style.transform = '';
        delete container._hadComputedTransform;
    } else {
        container.style.transform = container._originalStyles.transform;
    }
    container.style.willChange = container._originalStyles.willChange;
    delete container._originalStyles;
};

/** @param {ZoomableHTMLElement} img */
const convertToFixedPosition = (img) => {
    if (img.style.position === 'fixed') return;

    const rect = img.getBoundingClientRect();
    if (!img._originalPosition) {
        img._originalPosition = { left: rect.left, top: rect.top };
    }

    const container = getCharacterContainer(img);
    if (container) {
        if (!container._originalStyles) {
            container._originalStyles = {
                left: container.style.left,
                right: container.style.right,
                transform: container.style.transform,
                willChange: container.style.willChange
            };
        }
        const computedTransform = window.getComputedStyle(container).transform;
        if (computedTransform && computedTransform !== 'none') {
            container._hadComputedTransform = true;
            container.style.transform = 'none';
            if (!/\bcharacter-image-\d+\b/.test(container.className || '')) {
                container.style.left = '0';
                container.style.right = '0';
            }
        }
        container.style.willChange = 'auto';
    }

    img.style.position = 'fixed';
    img.style.left = `${rect.left}px`;
    img.style.top = `${rect.top}px`;
    img.style.zIndex = '950';

    constrainBottomEdge(img);
};

/**
 * True when the on-screen slice of a sprite is under a quarter of its unscaled
 * width or height. Scale is divided out so a zoom that still fills the viewport
 * does not count as lost.
 * @param {{ left: number, top: number, right: number, bottom: number, scale: number, viewportWidth: number, viewportHeight: number }} box
 */
export const spriteMostlyOffscreen = ({ left, top, right, bottom, scale, viewportWidth, viewportHeight }) => {
    const width = right - left;
    const height = bottom - top;
    if (width <= 0 || height <= 0) return false;

    const visibleWidth = Math.max(0, Math.min(right, viewportWidth) - Math.max(left, 0));
    const visibleHeight = Math.max(0, Math.min(bottom, viewportHeight) - Math.max(top, 0));
    const safeScale = Math.max(scale, 0.01);
    const naturalWidth = width / safeScale;
    const naturalHeight = height / safeScale;
    return visibleWidth < naturalWidth * RESET_MIN_VISIBLE_FRACTION
        || visibleHeight < naturalHeight * RESET_MIN_VISIBLE_FRACTION;
};

/** @param {ZoomableHTMLElement} img */
const isSpriteMostlyOffscreen = (img) => {
    if (img.style.position !== 'fixed') return false;
    const rect = img.getBoundingClientRect();
    return spriteMostlyOffscreen({
        left: rect.left,
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        scale: getCurrentScale(img),
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight
    });
};

const updateResetButtonVisibility = () => {
    const controls = document.querySelector('.global-zoom-controls');
    if (!controls) return;
    const lost = Array.from(queryCharacterMedia()).some((img) => isSpriteMostlyOffscreen(img));
    controls.style.display = lost ? 'block' : 'none';
};

let resetVisibilityResizeBound = false;

const bindResetVisibilityResize = () => {
    if (resetVisibilityResizeBound) return;
    resetVisibilityResizeBound = true;
    window.addEventListener('resize', updateResetButtonVisibility);
};

export function initializeImageZoom() {
    const images = queryCharacterMedia();

    /** @type {HTMLElement | null} */
    let globalControls = document.querySelector('.global-zoom-controls');
    if (!globalControls) {
        globalControls = document.createElement('div');
        globalControls.className = 'global-zoom-controls';
        globalControls.innerHTML = `
            <button class="zoom-button zoom-reset" aria-label="Reset zoom and positions">Reset</button>
        `;
        document.body.appendChild(globalControls);
        globalControls.style.cssText = `
            position: fixed;
            bottom: 20px;
            right: 20px;
            display: none;
            padding: 10px;
            z-index: 1000;
        `;
    }

    const globalResetButton = /** @type {ZoomableHTMLElement | null} */ (globalControls.querySelector('.zoom-reset'));
    if (globalResetButton && !globalResetButton._zoomResetBound) {
        globalResetButton._zoomResetBound = true;
        globalResetButton.addEventListener('click', (e) => {
            if (!e || e.isTrusted !== true) return;

            queryCharacterMedia().forEach((img) => {
                if (img._resetTransform) {
                    img._resetTransform();
                    return;
                }
                img.style.transform = 'scale(1)';
                img.style.transformOrigin = 'bottom center';
                img.style.position = '';
                img.style.left = '';
                img.style.top = '';
                img.style.zIndex = '';
                img.classList.remove('fixed-bottom');
                delete img._originalPosition;
            });

            document.querySelectorAll(CHARACTER_CONTAINER_SELECTOR).forEach((node) => {
                restoreContainerOriginalStyles(/** @type {ZoomableContainerElement} */ (node));
            });

            updateResetButtonVisibility();
            zoomStateMap.clear();
            globalControls.style.display = 'none';
        });
    }

    bindResetVisibilityResize();

    images.forEach((img) => {
        img._zoomKey = `char-${getContainerIndexForImage(img)}`;
        img.draggable = true;
        if (img._zoomInitialized) return;
        img._zoomInitialized = true;

        img.style.cursor = 'grab';
        img.style.touchAction = 'none';
        const container = getCharacterContainer(img);
        if (container) container.style.touchAction = 'none';

        let scale = 1;
        let isPanning = false;
        let hasActivatedPanning = false;
        let startX = 0;
        let startY = 0;
        let initialPinchDistance = 0;
        let initialScale = 1;
        let isPinching = false;
        let zoomTrackTimer = 0;
        img._scale = 1;

        const applyScale = () => {
            img._scale = scale;
            img.style.transform = `scale(${scale})`;
            img.style.transformOrigin = 'bottom center';
            img.style.cursor = 'grab';
            if (img.style.position === 'fixed') constrainBottomEdge(img);
            updateResetButtonVisibility();
            saveZoomState(img);
        };

        const scheduleZoomTrack = () => {
            window.clearTimeout(zoomTrackTimer);
            zoomTrackTimer = window.setTimeout(() => trackSprite('zoom', img), ZOOM_TRACK_IDLE_MS);
        };

        const capturePanOrigin = (clientX, clientY) => {
            if (img.style.position === 'fixed') {
                startX = clientX - (parseFloat(img.style.left) || 0);
                startY = clientY - (parseFloat(img.style.top) || 0);
                return;
            }
            const rect = img.getBoundingClientRect();
            startX = clientX - rect.left;
            startY = clientY - rect.top;
        };

        const beginPan = (clientX, clientY) => {
            isPanning = true;
            hasActivatedPanning = false;
            capturePanOrigin(clientX, clientY);
        };

        const movePan = (clientX, clientY) => {
            if (!isPanning) return;

            if (!hasActivatedPanning) {
                const currentLeft = img.style.position === 'fixed'
                    ? (parseFloat(img.style.left) || 0)
                    : (img.getBoundingClientRect().left || 0);
                const currentTop = img.style.position === 'fixed'
                    ? (parseFloat(img.style.top) || 0)
                    : (img.getBoundingClientRect().top || 0);
                if (
                    Math.abs(clientX - (startX + currentLeft)) < PAN_ACTIVATION_THRESHOLD_PX &&
                    Math.abs(clientY - (startY + currentTop)) < PAN_ACTIVATION_THRESHOLD_PX
                ) {
                    return;
                }

                if (img.style.position !== 'fixed') {
                    convertToFixedPosition(img);
                    img.classList.add('fixed-bottom');
                    img.style.transformOrigin = 'bottom center';
                } else {
                    img.style.zIndex = '950';
                }

                hasActivatedPanning = true;
                startX = clientX - (parseFloat(img.style.left) || 0);
                startY = clientY - (parseFloat(img.style.top) || 0);
            }

            img.style.left = `${clientX - startX}px`;
            img.style.top = `${clientY - startY}px`;
            constrainBottomEdge(img);
            updateResetButtonVisibility();
        };

        const endPan = () => {
            if (!isPanning) return false;

            isPanning = false;
            img.style.cursor = 'grab';

            if (hasActivatedPanning) {
                img.style.zIndex = '850';
                img.style.transform = `scale(${scale})`;
                img.style.transformOrigin = 'bottom center';
                img._scale = scale;
                const panContainer = getCharacterContainer(img);
                if (panContainer) panContainer.classList.add('user-positioned');
            }

            const wasPanningActivated = hasActivatedPanning;
            hasActivatedPanning = false;
            updateResetButtonVisibility();
            saveZoomState(img);
            if (wasPanningActivated) trackSprite('drag', img);
            return true;
        };

        img.addEventListener('wheel', /** @param {WheelEvent} e */ (e) => {
            e.preventDefault();
            e.stopPropagation();
            convertToFixedPosition(img);
            scale = Math.min(Math.max(scale + e.deltaY * -0.001, MIN_SCALE), MAX_SCALE);
            applyScale();
            scheduleZoomTrack();
        });

        img.addEventListener('mousedown', /** @param {MouseEvent} e */ (e) => {
            e.preventDefault();
            e.stopPropagation();
            beginPan(e.clientX, e.clientY);
            img.style.cursor = 'grabbing';
        });

        img.addEventListener('touchstart', /** @param {TouchEvent} e */ (e) => {
            if (e.touches.length === 1) {
                img._touchStartTime = Date.now();
                img._touchStartX = e.touches[0].clientX;
                img._touchStartY = e.touches[0].clientY;
            }

            if (e.touches.length === 2) {
                if (e.cancelable) e.preventDefault();
                e.stopPropagation();
                isPinching = true;
                isPanning = false;
                const touch1 = e.touches[0];
                const touch2 = e.touches[1];
                initialPinchDistance = Math.hypot(
                    touch2.clientX - touch1.clientX,
                    touch2.clientY - touch1.clientY
                );
                initialScale = scale;
                convertToFixedPosition(img);
                return;
            }

            if (e.touches.length !== 1) return;
            if (e.cancelable) e.preventDefault();
            e.stopPropagation();
            isPinching = false;
            beginPan(e.touches[0].clientX, e.touches[0].clientY);
        }, { passive: false });

        document.addEventListener('mousemove', /** @param {MouseEvent} e */ (e) => {
            if (!isPanning) return;
            movePan(e.clientX, e.clientY);
        });

        document.addEventListener('touchmove', /** @param {TouchEvent} e */ (e) => {
            if (isPinching && e.touches.length === 2) {
                if (e.cancelable) e.preventDefault();
                e.stopPropagation();
                const touch1 = e.touches[0];
                const touch2 = e.touches[1];
                const pinchScale = Math.hypot(
                    touch2.clientX - touch1.clientX,
                    touch2.clientY - touch1.clientY
                ) / initialPinchDistance;
                scale = Math.min(Math.max(initialScale * pinchScale, MIN_SCALE), MAX_SCALE);
                applyScale();
                return;
            }

            if (!isPanning || e.touches.length !== 1) return;
            if (e.cancelable) e.preventDefault();
            movePan(e.touches[0].clientX, e.touches[0].clientY);
        }, { passive: false });

        document.addEventListener('mouseup', () => {
            endPan();
        });

        document.addEventListener('touchend', () => {
            if (isPinching) {
                isPinching = false;
                updateResetButtonVisibility();
                saveZoomState(img);
                trackSprite('zoom', img);
                return;
            }
            endPan();
        });

        img.addEventListener('keydown', /** @param {KeyboardEvent} e */ (e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            const syntheticEvent = {
                preventDefault: () => {},
                stopPropagation: () => {},
                target: img
            };
            triggerCharacterInteraction(img, syntheticEvent);
        });

        img.addEventListener('click', /** @param {MouseEvent} e */ (e) => {
            const target = /** @type {Element | null} */ (e.target);
            if (target?.closest?.('#mobile-voice-play-btn')) return;
            triggerCharacterInteraction(img, e, { trackClick: true });
        });

        img.addEventListener('touchend', /** @param {TouchEvent} e */ (e) => {
            const touch = e.changedTouches[0];
            const touchDuration = Date.now() - (img._touchStartTime || 0);
            const touchMovement = Math.hypot(
                touch.clientX - (img._touchStartX || 0),
                touch.clientY - (img._touchStartY || 0)
            );
            if (touchDuration >= 300 || touchMovement >= 10) return;

            if (window.innerWidth <= 768) {
                const imgRect = img.getBoundingClientRect();
                const centerX = imgRect.left + imgRect.width / 2;
                const centerY = imgRect.top + imgRect.height / 2;
                const halfWidth = imgRect.width / 4;
                const halfHeight = imgRect.height / 4;
                const isWithinCenterHalf = touch.clientX >= (centerX - halfWidth) &&
                    touch.clientX <= (centerX + halfWidth) &&
                    touch.clientY >= (centerY - halfHeight) &&
                    touch.clientY <= imgRect.bottom;
                if (!isWithinCenterHalf) return;
            }

            const touchedElement = document.elementFromPoint(touch.clientX, touch.clientY);
            if (touchedElement && (
                touchedElement.id === 'mobile-voice-play-btn' ||
                touchedElement.closest('#mobile-voice-play-btn') ||
                touchedElement.id === 'mic-send-button-floating' ||
                touchedElement.closest('#mic-send-button-floating')
            )) {
                return;
            }

            const syntheticEvent = {
                preventDefault: () => {},
                stopPropagation: () => {},
                target: img
            };
            triggerCharacterInteraction(img, syntheticEvent, { trackClick: true });
        });

        img._resetTransform = () => {
            scale = 1;
            img._scale = 1;
            img.style.transform = 'scale(1)';
            img.style.transformOrigin = 'bottom center';
            img.style.position = '';
            img.style.left = '';
            img.style.top = '';
            img.style.zIndex = '';
            img.style.width = '';
            img.style.height = '';
            img.style.cursor = 'grab';
            img.classList.remove('fixed-bottom');
            delete img._originalPosition;

            const resetContainer = getCharacterContainer(img);
            if (resetContainer) restoreContainerOriginalStyles(resetContainer);

            if (img._zoomKey) zoomStateMap.delete(img._zoomKey);
        };
    });

    updateResetButtonVisibility();
}

/** @param {ZoomableHTMLElement} img */
function saveZoomState(img) {
    let key = img._zoomKey;
    if (!key) {
        key = `char-${getContainerIndexForImage(img)}`;
        img._zoomKey = key;
    }

    zoomStateMap.set(key, {
        scale: getCurrentScale(img),
        position: img.style.position,
        left: img.style.left,
        top: img.style.top,
        width: img.style.width,
        height: img.style.height,
        transform: img.style.transform,
        transformOrigin: img.style.transformOrigin,
        zIndex: img.style.zIndex,
        isFixedBottom: img.classList.contains('fixed-bottom'),
        containerCount: countCharacterContainers(),
        orientation: window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape',
        viewportW: window.innerWidth,
        viewportH: window.innerHeight
    });
}

/** @param {ZoomableHTMLElement} img */
export function applySavedState(img) {
    if (!img) return;

    img._zoomKey = `char-${getContainerIndexForImage(img)}`;
    const state = zoomStateMap.get(img._zoomKey);
    if (!state) return;

    img._scale = state.scale;

    const layoutChanged = typeof state.containerCount === 'number' && state.containerCount !== countCharacterContainers();
    const currentOrientation = window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape';
    const orientationChanged = !!state.orientation && state.orientation !== currentOrientation;

    if (layoutChanged || orientationChanged) {
        img.style.transform = `scale(${state.scale || 1})`;
        img.style.transformOrigin = 'bottom center';

        if (state.position === 'fixed' && (state.left || state.top)) {
            const savedLeft = parseFloat(state.left || '0') || 0;
            const savedTop = parseFloat(state.top || '0') || 0;
            const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
            img.style.position = 'fixed';
            img.style.left = `${clamp(savedLeft, 0, Math.max(0, window.innerWidth - 50))}px`;
            img.style.top = `${clamp(savedTop, 0, Math.max(0, window.innerHeight - 80))}px`;
            if (state.zIndex) img.style.zIndex = state.zIndex;
            img.classList.toggle('fixed-bottom', !!state.isFixedBottom);
            constrainBottomEdge(img);
            const savedContainer = getCharacterContainer(img);
            savedContainer?.classList.add('user-positioned');
        } else {
            img.style.position = '';
            img.style.left = '';
            img.style.top = '';
            img.style.zIndex = '';
            img.classList.remove('fixed-bottom');
        }
    } else {
        if (state.position) img.style.position = state.position;
        if (state.left) img.style.left = state.left;
        if (state.top) img.style.top = state.top;
        if (state.width) img.style.width = state.width;
        if (state.height) img.style.height = state.height;
        if (state.transform) {
            img.style.transform = state.transform;
        } else if (state.scale !== 1) {
            img.style.transform = `scale(${state.scale})`;
        } else {
            img.style.transform = 'scale(1)';
        }
        img.style.transformOrigin = state.transformOrigin || 'bottom center';
        if (state.zIndex) img.style.zIndex = state.zIndex;
        img.classList.toggle('fixed-bottom', !!state.isFixedBottom);
        if (state.position === 'fixed') constrainBottomEdge(img);
    }

    img.style.cursor = 'grab';
    img.draggable = true;
}

export function saveAllZoomStates() {
    queryCharacterMedia().forEach((img) => {
        if (img._zoomKey) saveZoomState(img);
    });
}

export function clearAllZoomStates() {
    zoomStateMap.clear();
    queryCharacterMedia().forEach((img) => {
        if (img._resetTransform) img._resetTransform();
    });
}
